import React, { FunctionComponent } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Dt,
  Typography
} from '../../../../components/v2/styled';

import { Appointment } from '../../../../models/Appointment';
import { User } from '../../../../models/User';

const { DateTime } = require("luxon");

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

type Props = {
  object: User;
  setPremiumNutriAppointmentOpen: (b:boolean) => void;
}

export const CoachUserWidgetsAppointmentsSmall: FunctionComponent<Props> = ({
  object,
  setPremiumNutriAppointmentOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  let soonCount = 0;
  let futureCount = 0;
  let pastCount = 0;

  let is_nutri_appointment = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'nutri_appointment'});

  // Coach-level widget settings (the `cta` goto-button toggle and the `mode`
  // body picker, defaults on/stats). Stored on
  // login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.appointmentsSmallValueKey];
  const showCta = HelpersClientWidgets.getAppointmentsSmallCtaEnabled(cfg);
  const mode = HelpersClientWidgets.getAppointmentsSmallMode(cfg);

  const fmtDay = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  }
  const fmtTime = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("t", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  }

  // Count-only queries feeding the stats body. `d.count` is the total matching
  // the filter regardless of per_page. Only fetched in stats mode.
  const {
    data: appointmentsSoon,
    // isLoading: appointmentsSoonLoading,
    // isError: appointmentsSoonError,
    // isSuccess: appointmentsSoonSuccess,
    // refetch: appointmentsSoonRefetch,
  } = useQuery({
    queryKey: ["appointments_soon", object?.id],
    queryFn: () =>
      fetch_all_with_count<Appointment>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'appointment',
          fields: [
            'id', 'start_time'
          ],
          page: 0,
          per_page: 1,
          order: "start_time ASC",
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id,
              soon: 1
            }
          }
        },
        login
      ),
      select: (d) => {
        soonCount = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!object && object.type === "User::User",
  });
  const {
    data: appointmentsFuture,
    // isLoading: appointmentsFutureLoading,
    // isError: appointmentsFutureError,
    // isSuccess: appointmentsFutureSuccess,
    // refetch: appointmentsFutureRefetch,
  } = useQuery({
    queryKey: ["appointments_future", object?.id],
    queryFn: () =>
      fetch_all_with_count<Appointment>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'appointment',
          fields: [
            'id', 'start_time'
          ],
          page: 0,
          per_page: 1,
          order: "start_time ASC",
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id,
              future: 1
            }
          }
        },
        login
      ),
      select: (d) => {
        futureCount = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!object && object.type === "User::User",
  });
  const {
    // data: appointmentsPast,
    // isLoading: appointmentsPastLoading,
    // isError: appointmentsPastError,
    // isSuccess: appointmentsPastSuccess,
    // refetch: appointmentsPastRefetch,
  } = useQuery({
    queryKey: ["appointments_past_count", object?.id],
    queryFn: () =>
      fetch_all_with_count<Appointment>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'appointment',
          fields: [
            'id'
          ],
          page: 0,
          per_page: 1,
          order: "start_time DESC",
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id,
              past: 1
            }
          }
        },
        login
      ),
      select: (d) => {
        pastCount = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!object && object.type === "User::User",
  });

  const nextAppointmentStart = appointmentsSoon?.[0]?.start_time || appointmentsFuture?.[0]?.start_time;

  // Last-5 list body: newest first, same query shape as the appointments
  // page's past list. Only fetched in list mode.
  const {
    data: lastAppointments,
  } = useQuery({
    queryKey: ["appointments_last", object?.id],
    queryFn: () =>
      fetch_all_with_count<Appointment>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'appointment',
          fields: [
            'id', 'start_time', 'is_cancelled', 'is_noshow',
            'appointment_type'
          ],
          sub_fields: {
            'appointment_type': ['id', 'name']
          },
          page: 0,
          per_page: 5,
          order: "start_time DESC",
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id
            }
          }
        },
        login
      ),
      select: (d) => {
        pastCount = d.count;
        return d.result;
      },
    enabled: mode === 'list' && !!object && object.type === "User::User",
  });

  const lastFive = (lastAppointments || []).slice(0, 5);

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row
        (space-saving — the old fullwidth bottom button cost a full row).
        Gated by the coach's `cta` setting; the premium lock uses the v2
        Button's built-in outlined + star treatment. */}
    {showCta && <Button
      label={t("coach.general.actions.to_appointments", "Naar de afspraken")}
      id="goto_calendar"
      text
      size="small"
      premiumLocked={!is_nutri_appointment}
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        if (!!is_nutri_appointment) {
          navigate(`/user/${object.id}/appointments`, {state: {user_id: object.id}});
        } else {
          setPremiumNutriAppointmentOpen(true);
        }
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.appointments")}</Typography>
        <Divider />
      </Grid>

      {/* Stats body: one i18n plural pair per metric (past / future),
          separated by a middot. Future = soon + future (the filters are
          disjoint: soon covers the imminent window, future everything
          beyond), matching the old title-count sum. The middot + future part
          drop away when nothing is upcoming; {{date}}/{{time}} are the
          start_time of the first soon appointment, falling back to the first
          future one. Only shown when there's at least one appointment. */}
      {mode === 'stats' && (pastCount + soonCount + futureCount) > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {`${t("coach.submenu.detail.appointments_past_count", {count: pastCount})}${(soonCount + futureCount) > 0 ? ` · ${t("coach.submenu.detail.appointments_future_count", {count: soonCount + futureCount, date: fmtDay(nextAppointmentStart), time: fmtTime(nextAppointmentStart)})}` : ''}`}
        </Typography>
      </Grid>}

      {/* List body: last 5 appointments (newest first), a Divider between
          consecutive rows. Cancelled/noshow rows are dimmed + struck through
          like the appointments page's past list. */}
      {mode === 'list' && lastFive.map((appointment, idx) => <React.Fragment key={appointment.id}>
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={appointment.is_cancelled || appointment.is_noshow ? {
              color: 'text.disabled',
              textDecoration: 'line-through'
            } : {}}
          >
            {appointment.appointment_type?.name || '-'}
          </Typography>
          <Typography variant="caption" sx={{color: 'text.secondary'}}>
            <Dt d={appointment.start_time} f="cccc DDD - t" />
          </Typography>
        </Grid>
        {idx < lastFive.length - 1 && <Grid item xs={12}>
          <Divider />
        </Grid>}
      </React.Fragment>)}
    </Grid>
  </Paper>;
}
